import React, { Children, isValidElement, type ReactNode } from 'react';

import { useInstantRef, useOutsideClick } from '@pkg/hooks';
import { useMediaQuery } from '@pkg/providers';

import { useLockBodyScroll } from '@app/helpers';

import { ModalCloseButtonPositions, ModalPaddingSizes, ModalVariants } from './Modal.types';
import { CloseAction, ModalHeader, ModalLayout } from './components';

import { Card, CardWrapper, Content, Interior, ModalWrapper } from './styles';

export interface ModalProps {
    children: ReactNode;
    footer?: ReactNode;
    // TODO: Remove `height` prop.
    height?: number;
    width?: string;
    mobileDarkBackground?: boolean;
    fullScreenMobile?: boolean;
    padded?: boolean;
    paddingSize?: ModalPaddingSizes;
    variant?: ModalVariants;
    closeButtonPosition?: ModalCloseButtonPositions;
    onClose: () => void;
}

export function Modal({
    children,
    height,
    width,
    fullScreenMobile = true,
    mobileDarkBackground,
    onClose,
    footer,
    padded = false,
    paddingSize = ModalPaddingSizes.Normal,
    variant = ModalVariants.Fantasy,
    closeButtonPosition = ModalCloseButtonPositions.Corner,
}: ModalProps) {
    const { tablet } = useMediaQuery();
    const [cardElement, cardRef] = useInstantRef<HTMLDivElement>();

    useLockBodyScroll(true);
    useOutsideClick(cardElement, onClose, { disabled: !tablet });

    let hasHeader = false;
    Children.forEach(children, (child) => {
        if (isValidElement(child) && child?.type === ModalHeader) {
            hasHeader = true;
        }
    });

    return (
        <ModalWrapper>
            <CardWrapper>
                <Card
                    ref={cardRef}
                    tabIndex={-1}
                    $fullScreenMobile={fullScreenMobile}
                    $maxHeight={tablet ? height : undefined}
                    $maxWidth={tablet ? width : undefined}
                    $variant={variant}
                >
                    <Interior $fullScreenMobile={fullScreenMobile} $mobileDarkBackground={mobileDarkBackground}>
                        {/*TODO export & use Close action properly, from Header*/}

                        <Content
                            $padded={padded}
                            $paddingSize={paddingSize}
                            $hasHeader={hasHeader}
                            $closeButtonPosition={closeButtonPosition}
                        >
                            <CloseAction position={closeButtonPosition} onClose={onClose} />
                            {children}
                        </Content>
                        {footer}
                    </Interior>
                </Card>
            </CardWrapper>
        </ModalWrapper>
    );
}

Modal.Layout = ModalLayout;
Modal.Header = ModalHeader;
